<template>
  <d-page-layout :hasSearch="false">
    <template slot="heading">
      <v-breadcrumbs class="pa-0" :items="items"></v-breadcrumbs>
    </template>

    <template slot="content">
      <d-loading v-if="loading.page"></d-loading>
      <template v-else>
        <div
          v-for="project in myProjects?.projects"
          :key="project.id"
          class="mb-1"
        >
          <v-row>
            <v-col cols="12" class="mb-2 border-bottom pb-1">
              <h6 class="text-h6">
                <span>{{ project.name }}</span>
                <span class="body-1 ml-3">{{ project.description }}</span>
              </h6>
            </v-col>
            <v-col cols="12">
              <v-expansion-panels multiple accordion>
                <v-expansion-panel
                  class="elevation-0 mb-2"
                  :class="`${getMethodColor(
                    api.httpMethodType.value
                  )}-accordion`"
                  v-for="api in project.apis"
                  :key="api.id"
                >
                  <v-expansion-panel-header>
                    <div class="w-100">
                      <p class="body-1 font-weight-bold mr-2 mb-0">
                        {{ api.name }}
                        <span class="mb-0 body-2 font-weight-regular">
                          {{ api.description }}
                        </span>
                      </p>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="d-flex align-center my-3">
                      <v-chip
                        label
                        small
                        class="font-weight-bold white--text"
                        >{{
                          api?.httpMethodType?.displayText?.toUpperCase()
                        }}</v-chip
                      >
                      <p class="mb-0 subtitle-1 mx-2 font-weight-bold">
                        {{ generatedURL(api?.url) }}
                      </p>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            color="gray darken-2"
                            icon
                            @click="copyToken(generatedURL(api?.url))"
                            small
                          >
                            <v-icon size="18"> mdi-content-copy </v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          copied
                            ? $t("defaults.button.copied")
                            : $t("defaults.button.copy")
                        }}</span>
                      </v-tooltip>
                    </div>

                    <div class="mb-3">
                      <h6 class="pb-1 subtitle-1 font-weight-bold mb-1">
                        {{ $t("apis.text.headers") }}:
                      </h6>
                      <v-row no-gutters>
                        <v-col cols="12" class="py-1">
                          <v-row>
                            <v-col cols="4"
                              ><span class="subtitle-2 font-weight-bold">{{
                                $t("apis.text.name")
                              }}</span></v-col
                            >
                            <v-col cols="6"
                              ><span class="subtitle-2 font-weight-bold">{{
                                $t("apis.text.description")
                              }}</span></v-col
                            >
                          </v-row>
                        </v-col>

                        <v-col cols="12" class="py-1">
                          <v-row>
                            <v-col cols="4">
                              <span
                                class="subtitle-2 gray--text text--darken-3"
                              >
                                {{
                                  myProjects.authorizationToken
                                    .authorizationHeaderKey
                                }}
                              </span>
                            </v-col>
                            <v-col cols="6">
                              <p class="body-2 gray--text text--darken-3 html">
                                {{ $t("apis.text.token-header") }}
                              </p>
                            </v-col>
                          </v-row>
                        </v-col>

                        <template v-if="api?.headers?.length > 0">
                          <v-col
                            cols="12"
                            class="py-1"
                            v-for="(header, i) in api.headers"
                            :key="i"
                          >
                            <v-row>
                              <v-col cols="4">
                                <span
                                  class="subtitle-2 gray--text text--darken-3"
                                >
                                  {{ header.key }}
                                </span>
                              </v-col>
                              <v-col cols="6">
                                <p
                                  class="body-2 gray--text text--darken-3 html"
                                >
                                  {{ header.description }}
                                </p>
                              </v-col>
                            </v-row>
                          </v-col>
                        </template>
                      </v-row>
                    </div>

                    <div class="mb-3" v-if="api?.parameters?.length > 0">
                      <h6 class="pb-1 subtitle-1 font-weight-bold mb-1">
                        {{ $t("apis.text.parameters") }}:
                      </h6>
                      <v-row no-gutters>
                        <v-col cols="12" class="py-1">
                          <v-row>
                            <v-col cols="4"
                              ><span class="subtitle-2 font-weight-bold">{{
                                $t("apis.text.name")
                              }}</span></v-col
                            >
                            <v-col cols="6"
                              ><span class="subtitle-2 font-weight-bold">{{
                                $t("apis.text.description")
                              }}</span></v-col
                            >
                          </v-row>
                        </v-col>

                        <v-col
                          cols="12"
                          class="py-1"
                          v-for="(parameter, i) in api.parameters"
                          :key="i"
                        >
                          <v-row>
                            <v-col cols="4">
                              <span
                                class="subtitle-2 gray--text text--darken-3"
                              >
                                {{ parameter.key }}
                              </span>
                            </v-col>
                            <v-col cols="6">
                              <p class="body-2 gray--text text--darken-3 html">
                                {{ parameter.description }}
                              </p>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </div>

                    <div class="mb-3" v-if="api.requestBody">
                      <h6 class="pb-1 subtitle-1 font-weight-bold mb-1">
                        {{ $t("apis.text.body") }}:
                      </h6>
                      <v-row no-gutters>
                        <v-col cols="12" class="py-1">
                          <BaseJsonEditor
                            :content="
                              api.requestBody != undefined
                                ? { text: api.requestBody }
                                : undefined
                            "
                            mode="text"
                            :mainMenuBar="false"
                            :navigationBar="false"
                            :statusBar="false"
                            readOnly
                          ></BaseJsonEditor>
                        </v-col>
                      </v-row>
                    </div>

                    <div v-if="api?.responses?.length > 0">
                      <h6 class="pb-1 subtitle-1 font-weight-bold mb-1">
                        {{ $t("apis.text.responses") }}:
                      </h6>
                      <v-row no-gutters>
                        <template>
                          <v-col cols="12" class="py-1">
                            <v-row>
                              <v-col cols="4"
                                ><span class="subtitle-2 font-weight-bold">{{
                                  $t("apis.text.code")
                                }}</span></v-col
                              >
                              <v-col cols="6"
                                ><span class="subtitle-2 font-weight-bold">{{
                                  $t("apis.text.description")
                                }}</span></v-col
                              >
                            </v-row>
                          </v-col>

                          <v-col
                            cols="12"
                            class="mt-2"
                            v-for="(response, i) in api.responses"
                            :key="i"
                            :class="{ 'mb-2': i < api.responses.length - 1 }"
                          >
                            <v-row>
                              <v-col cols="4">
                                <span
                                  class="subtitle-2 gray--text text--darken-3"
                                >
                                  {{ response.code }}
                                </span>
                              </v-col>
                              <v-col cols="6">
                                <BaseJsonEditor
                                  :content="
                                    response.description != undefined
                                      ? { text: response.description }
                                      : undefined
                                  "
                                  mode="text"
                                  :mainMenuBar="false"
                                  :navigationBar="false"
                                  :statusBar="false"
                                  readOnly
                                ></BaseJsonEditor>
                              </v-col>
                            </v-row>
                          </v-col>
                        </template>
                      </v-row>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </div>
      </template>
    </template>
  </d-page-layout>
</template>
    <script>
import { getUserProjectandApis } from "@/services/token";

import BaseJsonEditor from "@/components/BaseJsonEditor.vue";

export default {
  components: { BaseJsonEditor },
  data: (vm) => ({
    items: [
      {
        text: vm.$t("apis.page-heading"),
        disabled: true,
      },
    ],
    loading: {
      page: false,
    },
    myProjects: null,
    copied: false,
  }),
  computed: {
    userId() {
      return this.$route.params.id;
    },
  },
  methods: {
    async getUserProjectandApis() {
      try {
        this.myProjects = await getUserProjectandApis();
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      }
    },
    generatedURL(url) {
      return `${process.env.VUE_APP_BACKEND_URL}${url}`;
    },
    copyToken(token) {
      const textarea = document.createElement("textarea");
      textarea.value = token;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      this.copied = true;

      setTimeout(() => {
        this.copied = false;
      }, 1000);
    },
  },
  async mounted() {
    try {
      this.loading.page = true;
      await this.getUserProjectandApis();
    } finally {
      this.loading.page = false;
    }
  },
};
</script>
    
<style lang="scss">
.info-accordion {
  background: rgba(97, 175, 254, 0.1) !important;
  border: 1px solid #61affe;

  & .v-expansion-panel-header--active {
    border-bottom: 1px solid #61affe;
  }

  & .v-expansion-panel-content h6 {
    border-bottom: 1px solid #61affe;
  }

  & .v-chip {
    background-color: #61affe !important;
    color: white;
  }
}

.warning-accordion {
  background: rgba(252, 161, 48, 0.1) !important;
  border: 1px solid #fca130;

  & .v-expansion-panel-header--active {
    border-bottom: 1px solid #fca130;
  }

  & .v-expansion-panel-content h6 {
    border-bottom: 1px solid #fca130;
  }

  & .v-chip {
    background-color: #fca130 !important;
    color: white;
  }
}

.success-accordion {
  background: rgba(73, 204, 144, 0.1) !important;
  border: 1px solid #49cc90;

  & .v-expansion-panel-header--active {
    border-bottom: 1px solid #49cc90;
  }

  & .v-expansion-panel-content h6 {
    border-bottom: 1px solid #49cc90;
  }

  & .v-chip {
    background-color: #49cc90 !important;
    color: white;
  }
}

.error-accordion {
  background: rgba(249, 62, 62, 0.1) !important;
  border: 1px solid #f93e3e;

  & .v-expansion-panel-header--active {
    border-bottom: 1px solid #f93e3e;
  }

  & .v-expansion-panel-content h6 {
    border-bottom: 1px solid #f93e3e;
  }

  & .v-chip {
    background-color: #f93e3e !important;
    color: white;
  }
}
</style>